import JSZip from "jszip";
import { chapterContentToPlainText, normalizeChapterContentForStorage } from "./chapterContent";
import * as phase2 from "./phase2";
import * as phase3 from "./phase3";
import { normalizeWritingGoalWords, type Book, type Chapter, type CompatContext, type PreflightResult, type RevisionSnapshot } from "./models";
import {
  dataUrlToUint8Array,
  getResearchState,
  uint8ArrayToDataUrl,
  withResearchInMetadata,
  type ResearchAsset,
} from "./researchAssets";
import { normalizeThemeMode } from "./uiThemes";
import { storyCraftExportTextForChapter } from "./storyCraftFeedback";
import {
  defaultTitleForKind,
  isBookContentKind,
  withSpineOrderAndNumbers,
  type BookContentKind,
} from "./bookSpine";
import {
  bibliographyTitleForStyle,
  defaultBodyTitle,
  metadataForNewBook,
  resolveBookTypeProfile,
} from "./bookType";
import {
  chapterCharacterTabText,
  getCharactersFromBookMetadata,
  normalizeCharacterRoster,
  rosterFromExtractedJson,
  seedRosterFromChapterTabs,
  sortCharactersMajorFirst,
  withCharactersInMetadata,
} from "./characterRoster";
import {
  chapterLocationTabText,
  getLocationRelationshipsFromMetadata,
  getLocationsFromBookMetadata,
  normalizeLocationRoster,
  rosterFromExtractedLocationsJson,
  seedLocationsFromChapterTabs,
  sortLocations,
  withLocationsInMetadata,
} from "./locationRoster";
import {
  chapterTimelineTabText,
  getTimelineBeatsFromBookMetadata,
  getTimelineIssuesFromBookMetadata,
  getTimelineRemovalsFromMetadata,
  normalizeTimelineBeatRoster,
  normalizeTimelineTimingIssues,
  rosterFromExtractedTimelineJson,
  seedBeatsFromChapterTabs,
  sortBeatsChronologically,
  withTimelineInMetadata,
} from "./timelineRoster";
import {
  chapterThemesMotifsTabText,
  getMotifsFromBookMetadata,
  getSymbolsFromBookMetadata,
  getThemesFromBookMetadata,
  normalizeMotifRoster,
  normalizeSymbolRoster,
  normalizeThemeRoster,
  rosterFromExtractedThemesJson,
  seedThemesFromChapterTabs,
  themesToExtractedJson,
  withThemesMotifsInMetadata,
} from "./themeRoster";

/** 1.4: spine `kind` + `chapterNumber` on chapters (front/back matter in one ordered list). */
const COMPAT_VERSION = "1.4";
const KNOWN_TOP_LEVEL_FILES = new Set([
  "manifest.json",
  "settings.json",
  "metadata.json",
  "outline.json",
  "songs.json",
  "beta-questionnaire.json",
]);

function nowIso(): string {
  return new Date().toISOString();
}

function mimeFromStorageKey(key: string, fallback = "application/octet-stream"): string {
  if (/\.png$/i.test(key)) return "image/png";
  if (/\.jpe?g$/i.test(key)) return "image/jpeg";
  if (/\.webp$/i.test(key)) return "image/webp";
  if (/\.gif$/i.test(key)) return "image/gif";
  if (/\.pdf$/i.test(key)) return "application/pdf";
  return fallback;
}

function hydrateResearchAssetsFromPassthrough(
  metadata: Record<string, unknown>,
  passthroughFiles: Record<string, Buffer>
): Record<string, unknown> {
  const state = getResearchState(metadata);
  if (!state.assets.length) return metadata;
  let changed = false;
  const assets: ResearchAsset[] = state.assets.map((asset) => {
    if (asset.previewDataUrl) return asset;
    const key = asset.storageKey.startsWith("assets/") ? asset.storageKey : `assets/${asset.id}`;
    const bytes = passthroughFiles[key];
    if (!bytes || asset.kind !== "image") return asset;
    changed = true;
    return {
      ...asset,
      previewDataUrl: uint8ArrayToDataUrl(
        bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes),
        asset.mimeType || mimeFromStorageKey(key, "image/png")
      ),
    };
  });
  return changed ? withResearchInMetadata(metadata, { assets }) : metadata;
}

function metadataForZip(metadata: Record<string, unknown>): Record<string, unknown> {
  const state = getResearchState(metadata);
  if (!state.assets.some((asset) => asset.previewDataUrl)) return metadata;
  return withResearchInMetadata(metadata, {
    assets: state.assets.map((asset) => {
      const { previewDataUrl: _preview, ...rest } = asset;
      return rest;
    }),
  });
}

function writeResearchBinariesToZip(zip: JSZip, metadata: Record<string, unknown>): void {
  const state = getResearchState(metadata);
  for (const asset of state.assets) {
    if (!asset.previewDataUrl) continue;
    const bytes = dataUrlToUint8Array(asset.previewDataUrl);
    if (!bytes?.length) continue;
    const key = asset.storageKey.startsWith("assets/") ? asset.storageKey : `assets/${asset.id}`;
    zip.file(key, bytes);
  }
}

/** Stable book id for cloud sync (stored in `manifest.json`). */
export function newStableBookId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") {
    return c.randomUUID();
  }
  return `book-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

function createDefaultChapter(
  order = 1,
  kind: BookContentKind = "chapter",
  title?: string
): Chapter {
  const id = `chapter-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
  return {
    id,
    title:
      title?.trim() ||
      (kind === "chapter" ? `Chapter ${order}` : defaultTitleForKind(kind)),
    caption: "",
    content: "",
    order,
    kind,
    chapterNumber: kind === "chapter" ? order : null,
    comments: [],
    notes: [],
    tabData: {
      storyCraft: "",
      characters: "",
      locations: "",
      timeline: "",
      themesMotifs: "",
      summary: "",
    },
    metadata: {
      status: "draft",
      tags: [],
      keywords: [],
      label: "",
      customFields: {},
    },
    createdAt: nowIso(),
    updatedAt: nowIso(),
  };
}

export function createDefaultBook(options?: {
  bookType?: string;
  structure?: string;
}): Book {
  const profile = resolveBookTypeProfile(options?.bookType, options?.structure);
  const body = createDefaultChapter(1, "chapter", defaultBodyTitle(profile, 1));
  const chapters = [body];
  if (profile.citations) {
    chapters.push(
      createDefaultChapter(2, "bibliography", bibliographyTitleForStyle(profile.citationStyle))
    );
  }
  return {
    id: newStableBookId(),
    title: "Untitled",
    author: "",
    description: "",
    createdAt: nowIso(),
    updatedAt: nowIso(),
    settings: {
      theme: "system",
      writingGoalWords: 0,
    },
    metadata: metadataForNewBook(profile),
    chapters: withSpineOrderAndNumbers(chapters),
    feedbackEndpoint: "",
    phase2: {
      snapshots: [],
      connectorStates: phase2.ensureConnectorStates({}),
      lastPreflight: null,
      pendingConflict: null,
    },
    phase3: phase3.createDefaultPhase3State(),
  };
}

async function readJson<T>(zip: JSZip, path: string, fallback: T): Promise<T> {
  const file = zip.file(path);
  if (!file) return fallback;
  try {
    return JSON.parse(await file.async("string"));
  } catch {
    return fallback;
  }
}

function normalizeChapterContent(value: unknown): string {
  return normalizeChapterContentForStorage(value);
}

function normalizeChapters(manifest: Record<string, unknown>, chapterRecords: Array<Record<string, unknown>>): Chapter[] {
  const chapterOrder = Array.isArray(manifest.chapterOrder)
    ? (manifest.chapterOrder as string[])
    : [];
  const byId = new Map<string, Chapter>();

  for (const raw of chapterRecords) {
    const rawId = raw?.id;
    if (!raw || typeof rawId !== "string" || !rawId) continue;
    const kind: BookContentKind = isBookContentKind(raw.kind) ? raw.kind : "chapter";
    const chapterNumberRaw = raw.chapterNumber ?? raw.chapter_number;
    const chapterNumber =
      chapterNumberRaw == null
        ? kind === "chapter"
          ? null
          : null
        : Number.isFinite(Number(chapterNumberRaw))
          ? Number(chapterNumberRaw)
          : null;
    byId.set(rawId, {
      id: String(raw.id),
      title: String(raw.title || (kind === "chapter" ? "Untitled Chapter" : defaultTitleForKind(kind))),
      caption: typeof raw.caption === "string" ? raw.caption : "",
      content: normalizeChapterContent(raw.contentJson ?? raw.content),
      order: Number.isFinite(raw.order) ? Number(raw.order) : 0,
      kind,
      chapterNumber: kind === "chapter" ? chapterNumber : null,
      comments: Array.isArray(raw.comments) ? raw.comments : [],
      notes: Array.isArray(raw.notes) ? raw.notes : [],
      tabData: {
        storyCraft: typeof raw.storyCraft === "string" ? raw.storyCraft : "",
        characters: typeof raw.characters === "string" ? raw.characters : "",
        locations: typeof raw.locations === "string" ? raw.locations : "",
        timeline: typeof raw.timelineNotes === "string" ? raw.timelineNotes : "",
        themesMotifs: typeof raw.themesMotifs === "string" ? raw.themesMotifs : "",
        summary: typeof raw.summary === "string" ? raw.summary : "",
      },
      metadata: {
        status: typeof raw.status === "string" ? (raw.status as Chapter["metadata"]["status"]) : "draft",
        tags: Array.isArray(raw.tags) ? raw.tags : [],
        keywords: Array.isArray(raw.keywords) ? raw.keywords : [],
        label: typeof raw.label === "string" ? raw.label : "",
        customFields:
          raw.customFields && typeof raw.customFields === "object"
            ? (raw.customFields as Record<string, unknown>)
            : {},
        ...(raw.syncBase && typeof raw.syncBase === "object"
          ? {
              syncBase: {
                rev:
                  typeof (raw.syncBase as { rev?: unknown }).rev === "number"
                    ? Number((raw.syncBase as { rev: number }).rev)
                    : 0,
                title:
                  typeof (raw.syncBase as { title?: unknown }).title === "string"
                    ? String((raw.syncBase as { title: string }).title)
                    : String(raw.title || ""),
                content:
                  typeof (raw.syncBase as { content?: unknown }).content === "string"
                    ? String((raw.syncBase as { content: string }).content)
                    : normalizeChapterContent(
                        (raw.syncBase as { content?: unknown }).content ?? raw.content
                      ),
              },
            }
          : {}),
      },
      variations: Array.isArray(raw.variations) ? raw.variations : [],
      rev: typeof raw.rev === "number" && Number.isFinite(raw.rev) ? Number(raw.rev) : undefined,
      baseRev:
        typeof raw.baseRev === "number" && Number.isFinite(raw.baseRev)
          ? Number(raw.baseRev)
          : typeof raw.rev === "number" && Number.isFinite(raw.rev)
            ? Number(raw.rev)
            : undefined,
      createdAt: typeof raw.createdAt === "string" ? raw.createdAt : nowIso(),
      updatedAt: typeof raw.updatedAt === "string" ? raw.updatedAt : nowIso(),
    });
  }

  const ordered: Chapter[] = [];
  for (const chapterId of chapterOrder) {
    if (byId.has(chapterId)) {
      const chapter = byId.get(chapterId);
      if (chapter) ordered.push(chapter);
      byId.delete(chapterId);
    }
  }

  for (const chapter of byId.values()) {
    ordered.push(chapter);
  }

  if (ordered.length === 0) {
    ordered.push(createDefaultChapter(1));
  }

  return withSpineOrderAndNumbers(ordered);
}

function mapLegacyExtractedToChapterTabs(chapters: Chapter[], extracted: Record<string, unknown>): void {
  const charText = Array.isArray(extracted.characters)
    ? extracted.characters
        .map((item) => {
          const record = item && typeof item === "object" ? (item as Record<string, unknown>) : {};
          return String(record.name || record.title || "");
        })
        .filter(Boolean)
        .join("\n")
    : "";
  const locText = Array.isArray(extracted.locations)
    ? extracted.locations
        .map((item) => {
          const record = item && typeof item === "object" ? (item as Record<string, unknown>) : {};
          return String(record.name || record.title || "");
        })
        .filter(Boolean)
        .join("\n")
    : "";
  const timelineText = Array.isArray(extracted.timeline)
    ? extracted.timeline
        .map((item) => {
          if (!item) return "";
          const record = item && typeof item === "object" ? (item as Record<string, unknown>) : {};
          const label = record.date || record.time || record.title || "";
          const detail = record.event || record.description || "";
          return [label, detail].filter(Boolean).join(" - ");
        })
        .filter(Boolean)
        .join("\n")
    : "";
  const summaryText = Array.isArray(extracted.summaries)
    ? extracted.summaries
        .map((item) => {
          if (typeof item === "string") return item;
          const record = item && typeof item === "object" ? (item as Record<string, unknown>) : {};
          return String(record.summary || record.text || "");
        })
        .filter(Boolean)
        .join("\n\n")
    : "";
  const storyCraftText = Array.isArray(extracted.storycraft)
    ? extracted.storycraft
        .map((item) => {
          const record = item && typeof item === "object" ? (item as Record<string, unknown>) : {};
          return String(record.message || record.feedback || "");
        })
        .filter(Boolean)
        .join("\n")
    : "";
  const themesRecord =
    extracted.themes && typeof extracted.themes === "object"
      ? (extracted.themes as Record<string, unknown>)
      : null;
  const themeText =
    themesRecord
      ? [
          ...(Array.isArray(themesRecord.themes) ? themesRecord.themes : []),
          ...(Array.isArray(themesRecord.motifs) ? themesRecord.motifs : []),
          ...(Array.isArray(themesRecord.symbols) ? themesRecord.symbols : []),
        ]
          .map((item) => {
            if (typeof item === "string") return item;
            const record = item && typeof item === "object" ? (item as Record<string, unknown>) : {};
            return String(record.name || record.title || "");
          })
          .filter(Boolean)
          .join("\n")
      : "";

  for (const chapter of chapters) {
    chapter.tabData = chapter.tabData || {};
    if (!chapter.tabData.characters && charText) chapter.tabData.characters = charText;
    if (!chapter.tabData.locations && locText) chapter.tabData.locations = locText;
    if (!chapter.tabData.timeline && timelineText) chapter.tabData.timeline = timelineText;
    if (!chapter.tabData.summary && summaryText) chapter.tabData.summary = summaryText;
    if (!chapter.tabData.storyCraft && storyCraftText) chapter.tabData.storyCraft = storyCraftText;
    if (!chapter.tabData.themesMotifs && themeText) chapter.tabData.themesMotifs = themeText;
  }
}

function buildExtractedRollups(chapters: Chapter[], bookMetadata?: Record<string, unknown>) {
  const splitLines = (text: unknown) =>
    String(text || "")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
  const uniq = (values: string[]) => Array.from(new Set(values));

  const roster = getCharactersFromBookMetadata(bookMetadata);
  const characters =
    roster.length > 0
      ? sortCharactersMajorFirst(roster)
      : uniq(chapters.flatMap((chapter) => splitLines(chapter.tabData?.characters))).map((name) => ({
          name,
        }));
  const locationRoster = getLocationsFromBookMetadata(bookMetadata);
  const locationRelationships = getLocationRelationshipsFromMetadata(bookMetadata);
  const locations =
    locationRoster.length > 0
      ? sortLocations(locationRoster).map((loc) => ({
          id: loc.id,
          name: loc.name,
          aliases: loc.aliases,
          description: loc.description,
          type: loc.type,
          parentLocationId: loc.parentLocationId,
          appearances: loc.appearances,
          firstAppearanceChapterId: loc.firstAppearanceChapterId,
          updatedAt: loc.updatedAt,
        }))
      : uniq(chapters.flatMap((chapter) => splitLines(chapter.tabData?.locations))).map((name) => ({
          name,
        }));
  const allSummaries = chapters
    .map((chapter) => String(chapter.tabData?.summary || "").trim())
    .filter(Boolean)
    .map((summary, index) => ({
      chapterId: chapters[index].id,
      summary,
    }));
  const allStoryCraft = chapters
    .flatMap((chapter) =>
      splitLines(
        storyCraftExportTextForChapter(
          bookMetadata,
          chapter.id,
          chapter.title,
          chapter.tabData?.storyCraft
        )
      )
    )
    .map((feedback) => ({ feedback }));
  const timelineBeats = getTimelineBeatsFromBookMetadata(bookMetadata);
  const timeline =
    timelineBeats.length > 0
      ? sortBeatsChronologically(timelineBeats).map((beat) => ({
          id: beat.id,
          summary: beat.summary,
          when: beat.when,
          sortKey: beat.sortKey,
          datePrecision: beat.datePrecision,
          beatKind: beat.beatKind,
          narrativePosition: beat.narrativePosition,
          chapterId: beat.chapterId,
          chapterIds: beat.chapterIds,
          note: beat.note,
          evidence: beat.evidence,
          updatedAt: beat.updatedAt,
        }))
      : uniq(chapters.flatMap((chapter) => splitLines(chapter.tabData?.timeline))).map((event) => ({
          event,
        }));
  const timelineIssues = getTimelineIssuesFromBookMetadata(bookMetadata);

  const themeRoster = getThemesFromBookMetadata(bookMetadata);
  const motifRoster = getMotifsFromBookMetadata(bookMetadata);
  const symbolRoster = getSymbolsFromBookMetadata(bookMetadata);
  const themes =
    themeRoster.length > 0 || motifRoster.length > 0 || symbolRoster.length > 0
      ? themesToExtractedJson(themeRoster, motifRoster, symbolRoster)
      : {
          themes: uniq(chapters.flatMap((chapter) => splitLines(chapter.tabData?.themesMotifs))),
          motifs: [] as string[],
          symbols: [] as string[],
          lastUpdated: nowIso(),
        };

  return {
    characters,
    locations,
    locationRelationships,
    timeline,
    timelineIssues,
    summaries: allSummaries,
    storycraft: allStoryCraft,
    themes,
  };
}

export async function loadBookFromBuffer(
  buffer: Buffer | Uint8Array | ArrayBuffer
): Promise<{ book: Book; compatContext: CompatContext }> {
  const zip = await JSZip.loadAsync(buffer);
  const manifest = await readJson<Record<string, unknown>>(zip, "manifest.json", {});
  const settings = await readJson<Record<string, unknown>>(zip, "settings.json", {});
  const metadata = await readJson<Record<string, unknown>>(zip, "metadata.json", {
    language: "en",
    keywords: [],
  });

  const chapterPaths = Object.keys(zip.files).filter(
    (key) => key.startsWith("chapters/") && key.endsWith(".json") && !zip.files[key].dir
  );
  const chapterRecords: Array<Record<string, unknown>> = [];
  for (const chapterPath of chapterPaths) {
    const chapterJson = await readJson(zip, chapterPath, null);
    if (chapterJson) chapterRecords.push(chapterJson);
  }

  const extracted = {
    characters: await readJson(zip, "extracted/characters.json", []),
    locations: await readJson(zip, "extracted/locations.json", []),
    timeline: await readJson(zip, "extracted/timeline.json", []),
    summaries: await readJson(zip, "extracted/summaries.json", []),
    storycraft: await readJson(zip, "extracted/storycraft.json", []),
    themes: await readJson(zip, "extracted/themes.json", { themes: [], motifs: [], symbols: [] }),
  };
  const manifestRecord = manifest as Record<string, unknown>;
  const settingsRecord = settings as Record<string, unknown>;
  const metadataRecord = metadata as Record<string, unknown>;

  const passthroughFiles: Record<string, Buffer> = {};
  const fileKeys = Object.keys(zip.files);
  for (const key of fileKeys) {
    const entry = zip.files[key];
    if (entry.dir) continue;
    const isKnownTopLevel = KNOWN_TOP_LEVEL_FILES.has(key);
    const isKnownFolder =
      key.startsWith("chapters/") ||
      key.startsWith("extracted/") ||
      key.startsWith("tabs/") ||
      key.startsWith("sync/");
    if (!isKnownTopLevel && !isKnownFolder) {
      passthroughFiles[key] = (await entry.async("uint8array")) as unknown as Buffer;
    }
  }

  const manifestBookId =
    typeof manifestRecord.id === "string" && manifestRecord.id.trim() ? String(manifestRecord.id).trim() : undefined;

  const book: Book = {
    ...(manifestBookId ? { id: manifestBookId } : {}),
    title: typeof manifestRecord.title === "string" ? manifestRecord.title : "Untitled",
    author: typeof manifestRecord.author === "string" ? manifestRecord.author : "",
    description: typeof manifestRecord.description === "string" ? manifestRecord.description : "",
    createdAt: typeof manifestRecord.createdAt === "string" ? manifestRecord.createdAt : nowIso(),
    updatedAt: typeof manifestRecord.updatedAt === "string" ? manifestRecord.updatedAt : nowIso(),
    settings: {
      theme: normalizeThemeMode(settingsRecord.theme),
      writingGoalWords: normalizeWritingGoalWords(settingsRecord.writingGoalWords),
    },
    metadata: hydrateResearchAssetsFromPassthrough(metadataRecord, passthroughFiles),
    chapters: normalizeChapters(manifest, chapterRecords),
    feedbackEndpoint:
      typeof settingsRecord.feedbackEndpoint === "string" ? settingsRecord.feedbackEndpoint : "",
    phase2: {
      snapshots: [],
      connectorStates: phase2.ensureConnectorStates({}),
      lastPreflight: null,
      pendingConflict: null,
    },
    phase3: phase3.createDefaultPhase3State(),
  };
  const phase2Metadata =
    metadataRecord.phase2 && typeof metadataRecord.phase2 === "object"
      ? (metadataRecord.phase2 as Record<string, unknown>)
      : {};
  book.phase2 = {
    snapshots: Array.isArray(phase2Metadata.snapshots)
      ? (phase2Metadata.snapshots as RevisionSnapshot[])
      : [],
    connectorStates: phase2.ensureConnectorStates(phase2Metadata.connectorStates),
    lastPreflight: phase2Metadata.lastPreflight
      ? (phase2Metadata.lastPreflight as PreflightResult)
      : null,
    pendingConflict:
      phase2Metadata.pendingConflict && typeof phase2Metadata.pendingConflict === "object"
        ? (phase2Metadata.pendingConflict as Book["phase2"]["pendingConflict"])
        : null,
  };
  book.phase3 = phase3.normalizePhase3State(metadataRecord.phase3);
  mapLegacyExtractedToChapterTabs(book.chapters, extracted);

  // Merge sync/base/<chapterId>.json sidecars into chapter.metadata.syncBase (additive).
  for (const chapter of book.chapters) {
    const sidecarPath = `sync/base/${chapter.id}.json`;
    if (!zip.files[sidecarPath] || zip.files[sidecarPath].dir) continue;
    const sidecar = await readJson<Record<string, unknown> | null>(zip, sidecarPath, null);
    if (!sidecar || typeof sidecar !== "object") continue;
    const content =
      typeof sidecar.content === "string"
        ? sidecar.content
        : sidecar.content != null
          ? normalizeChapterContent(sidecar.content)
          : "";
    if (!content) continue;
    chapter.metadata = {
      ...chapter.metadata,
      syncBase: {
        rev: typeof sidecar.rev === "number" && Number.isFinite(sidecar.rev) ? Number(sidecar.rev) : 0,
        title: typeof sidecar.title === "string" ? sidecar.title : chapter.title,
        content,
      },
    };
    if (typeof sidecar.rev === "number" && Number.isFinite(sidecar.rev)) {
      chapter.rev = Number(sidecar.rev);
      chapter.baseRev = Number(sidecar.rev);
    }
  }

  // Prefer structured roster already in metadata; else load from extracted/characters.json; else seed from tabs.
  const metaRoster = getCharactersFromBookMetadata(book.metadata as Record<string, unknown>);
  const extractedRoster = rosterFromExtractedJson(extracted.characters);
  let characters = metaRoster.length > 0 ? metaRoster : extractedRoster;
  if (characters.length === 0) {
    characters = seedRosterFromChapterTabs(book.chapters);
  }
  if (characters.length > 0) {
    book.metadata = withCharactersInMetadata(
      (book.metadata ?? {}) as Record<string, unknown>,
      normalizeCharacterRoster(characters)
    ) as Book["metadata"];
    for (const chapter of book.chapters) {
      const derived = chapterCharacterTabText(characters, chapter.id);
      if (derived) chapter.tabData.characters = derived;
    }
  }

  // Prefer structured locations in metadata; else extracted/locations.json; else seed from tabs.
  const metaLocations = getLocationsFromBookMetadata(book.metadata as Record<string, unknown>);
  const extractedLocations = rosterFromExtractedLocationsJson(extracted.locations);
  let locations = metaLocations.length > 0 ? metaLocations : extractedLocations;
  if (locations.length === 0) {
    locations = seedLocationsFromChapterTabs(book.chapters);
  }
  if (locations.length > 0) {
    const existingRelationships = getLocationRelationshipsFromMetadata(
      book.metadata as Record<string, unknown>
    );
    book.metadata = withLocationsInMetadata(
      (book.metadata ?? {}) as Record<string, unknown>,
      normalizeLocationRoster(locations),
      existingRelationships
    ) as Book["metadata"];
    for (const chapter of book.chapters) {
      const derived = chapterLocationTabText(locations, chapter.id);
      if (derived) chapter.tabData.locations = derived;
    }
  }

  // Prefer structured timeline in metadata; else extracted/timeline.json; else seed from tabs.
  const metaBeats = getTimelineBeatsFromBookMetadata(book.metadata as Record<string, unknown>);
  const extractedBeats = rosterFromExtractedTimelineJson(extracted.timeline);
  let timelineBeats = metaBeats.length > 0 ? metaBeats : extractedBeats;
  if (timelineBeats.length === 0) {
    timelineBeats = seedBeatsFromChapterTabs(book.chapters);
  }
  if (timelineBeats.length > 0) {
    const existingIssues = getTimelineIssuesFromBookMetadata(
      book.metadata as Record<string, unknown>
    );
    const existingRemovals = getTimelineRemovalsFromMetadata(
      book.metadata as Record<string, unknown>
    );
    book.metadata = withTimelineInMetadata(
      (book.metadata ?? {}) as Record<string, unknown>,
      normalizeTimelineBeatRoster(timelineBeats),
      normalizeTimelineTimingIssues(existingIssues),
      existingRemovals
    ) as Book["metadata"];
    for (const chapter of book.chapters) {
      const derived = chapterTimelineTabText(timelineBeats, chapter.id);
      if (derived) chapter.tabData.timeline = derived;
    }
  }

  // Prefer structured themes/motifs/symbols in metadata; else extracted/themes.json; else seed from tabs.
  const metaThemes = getThemesFromBookMetadata(book.metadata as Record<string, unknown>);
  const metaMotifs = getMotifsFromBookMetadata(book.metadata as Record<string, unknown>);
  const metaSymbols = getSymbolsFromBookMetadata(book.metadata as Record<string, unknown>);
  const extractedThemesRoster = rosterFromExtractedThemesJson(extracted.themes);
  let themes =
    metaThemes.length > 0 || metaMotifs.length > 0 || metaSymbols.length > 0
      ? metaThemes
      : extractedThemesRoster.themes;
  let motifs =
    metaThemes.length > 0 || metaMotifs.length > 0 || metaSymbols.length > 0
      ? metaMotifs
      : extractedThemesRoster.motifs;
  let symbols =
    metaThemes.length > 0 || metaMotifs.length > 0 || metaSymbols.length > 0
      ? metaSymbols
      : extractedThemesRoster.symbols;
  if (themes.length === 0 && motifs.length === 0 && symbols.length === 0) {
    themes = seedThemesFromChapterTabs(book.chapters);
  }
  if (themes.length > 0 || motifs.length > 0 || symbols.length > 0) {
    book.metadata = withThemesMotifsInMetadata(
      (book.metadata ?? {}) as Record<string, unknown>,
      normalizeThemeRoster(themes),
      normalizeMotifRoster(motifs),
      normalizeSymbolRoster(symbols)
    ) as Book["metadata"];
    for (const chapter of book.chapters) {
      const derived = chapterThemesMotifsTabText(themes, motifs, symbols, chapter.id);
      if (derived) chapter.tabData.themesMotifs = derived;
    }
  }

  const compatContext: CompatContext = {
    passthroughFiles,
  };

  return { book, compatContext };
}

export async function saveBookToBuffer(
  book: Book,
  compatContext: CompatContext | null
): Promise<{ buffer: Buffer; persistedBookId: string }> {
  const zip = new JSZip();
  const safeBook = book && typeof book === "object" ? book : createDefaultBook();
  const chapters = withSpineOrderAndNumbers(
    Array.isArray(safeBook.chapters) && safeBook.chapters.length > 0
      ? safeBook.chapters
      : [createDefaultChapter(1)]
  );

  const persistedBookId =
    typeof safeBook.id === "string" && safeBook.id.trim() ? String(safeBook.id).trim() : newStableBookId();

  const manifest = {
    version: COMPAT_VERSION,
    id: persistedBookId,
    title: safeBook.title || "Untitled",
    author: safeBook.author || "",
    description: safeBook.description || "",
    chapterOrder: chapters.map((chapter) => chapter.id),
    createdAt: safeBook.createdAt || nowIso(),
    updatedAt: nowIso(),
  };
  zip.file("manifest.json", JSON.stringify(manifest, null, 2));
  zip.file(
    "settings.json",
    JSON.stringify(
      {
        theme: normalizeThemeMode(safeBook.settings?.theme),
        writingGoalWords: normalizeWritingGoalWords(safeBook.settings?.writingGoalWords),
        feedbackEndpoint: safeBook.feedbackEndpoint || "",
      },
      null,
      2
    )
  );
  zip.file(
    "metadata.json",
    JSON.stringify(
      {
        ...metadataForZip((safeBook.metadata || { language: "en", keywords: [] }) as Record<string, unknown>),
        phase2: {
          snapshots: Array.isArray(safeBook.phase2?.snapshots) ? safeBook.phase2.snapshots : [],
          connectorStates: phase2.ensureConnectorStates(safeBook.phase2?.connectorStates),
          lastPreflight: safeBook.phase2?.lastPreflight || null,
          pendingConflict:
            safeBook.phase2?.pendingConflict && typeof safeBook.phase2.pendingConflict === "object"
              ? safeBook.phase2.pendingConflict
              : null,
        },
        phase3: phase3.normalizePhase3State(safeBook.phase3),
      },
      null,
      2
    )
  );

  const chaptersFolder = zip.folder("chapters");
  if (!chaptersFolder) {
    throw new Error("Failed to create chapters folder in zip archive");
  }
  for (let index = 0; index < chapters.length; index += 1) {
    const chapter = chapters[index];
    const contentJson = normalizeChapterContent(chapter.content || "");
    const kind: BookContentKind = isBookContentKind(chapter.kind) ? chapter.kind : "chapter";
    const normalized = {
      id: chapter.id || `chapter-${index + 1}`,
      title: chapter.title || (kind === "chapter" ? `Chapter ${index + 1}` : defaultTitleForKind(kind)),
      caption: typeof chapter.caption === "string" ? chapter.caption : "",
      /** Canonical TipTap JSON string (with paragraph ids). */
      content: contentJson,
      /** Plain-text mirror for older readers / tools that expect prose only. */
      contentPlain: chapterContentToPlainText(contentJson),
      order: index + 1,
      kind,
      chapterNumber: kind === "chapter" ? (chapter.chapterNumber ?? null) : null,
      comments: Array.isArray(chapter.comments) ? chapter.comments : [],
      notes: Array.isArray(chapter.notes) ? chapter.notes : [],
      storyCraft: storyCraftExportTextForChapter(
        safeBook.metadata as Record<string, unknown> | undefined,
        chapter.id || `chapter-${index + 1}`,
        chapter.title || `Chapter ${index + 1}`,
        chapter.tabData?.storyCraft
      ),
      characters: chapter.tabData?.characters || "",
      locations: chapter.tabData?.locations || "",
      timelineNotes: chapter.tabData?.timeline || "",
      themesMotifs: chapter.tabData?.themesMotifs || "",
      summary: chapter.tabData?.summary || "",
      status: chapter.metadata?.status || "draft",
      tags: Array.isArray(chapter.metadata?.tags) ? chapter.metadata.tags : [],
      keywords: Array.isArray(chapter.metadata?.keywords) ? chapter.metadata.keywords : [],
      label: chapter.metadata?.label || "",
      customFields:
        chapter.metadata?.customFields && typeof chapter.metadata.customFields === "object"
          ? chapter.metadata.customFields
          : {},
      variations: Array.isArray(chapter.variations) ? chapter.variations : [],
      rev: typeof chapter.rev === "number" ? chapter.rev : undefined,
      baseRev: typeof chapter.baseRev === "number" ? chapter.baseRev : undefined,
      syncBase: chapter.metadata?.syncBase ?? undefined,
      createdAt: chapter.createdAt || nowIso(),
      updatedAt: nowIso(),
    };
    chaptersFolder.file(`${normalized.id}.json`, JSON.stringify(normalized, null, 2));
  }

  // Persist last-acked merge bases as sync/base/<chapterId>.json sidecars.
  const syncBaseFolder = zip.folder("sync/base");
  if (syncBaseFolder) {
    for (const chapter of chapters) {
      const base = chapter.metadata?.syncBase;
      if (!base?.content) continue;
      const chapterId = chapter.id || "";
      if (!chapterId) continue;
      syncBaseFolder.file(
        `${chapterId}.json`,
        JSON.stringify(
          {
            rev: typeof base.rev === "number" ? base.rev : 0,
            title: base.title || chapter.title || "",
            content: normalizeChapterContent(base.content),
          },
          null,
          2
        )
      );
    }
  }

  const extractedFolder = zip.folder("extracted");
  if (!extractedFolder) {
    throw new Error("Failed to create extracted folder in zip archive");
  }
  const rollups = buildExtractedRollups(chapters, safeBook.metadata as Record<string, unknown> | undefined);
  extractedFolder.file("characters.json", JSON.stringify(rollups.characters, null, 2));
  extractedFolder.file("locations.json", JSON.stringify(rollups.locations, null, 2));
  if (rollups.locationRelationships.length > 0) {
    extractedFolder.file(
      "location-relationships.json",
      JSON.stringify(rollups.locationRelationships, null, 2)
    );
  }
  extractedFolder.file("timeline.json", JSON.stringify(rollups.timeline, null, 2));
  if (Array.isArray(rollups.timelineIssues) && rollups.timelineIssues.length > 0) {
    extractedFolder.file("timeline-issues.json", JSON.stringify(rollups.timelineIssues, null, 2));
  }
  extractedFolder.file("summaries.json", JSON.stringify(rollups.summaries, null, 2));
  extractedFolder.file("storycraft.json", JSON.stringify(rollups.storycraft, null, 2));
  extractedFolder.file("themes.json", JSON.stringify(rollups.themes, null, 2));

  writeResearchBinariesToZip(zip, (safeBook.metadata || {}) as Record<string, unknown>);

  const passthrough = compatContext && compatContext.passthroughFiles ? compatContext.passthroughFiles : {};
  for (const [filePath, contents] of Object.entries(passthrough)) {
    if (!zip.file(filePath)) {
      zip.file(filePath, contents as Buffer);
    }
  }

  const bytes = await zip.generateAsync({ type: "uint8array" });
  const buffer =
    typeof Buffer !== "undefined" ? Buffer.from(bytes) : (bytes as unknown as Buffer);
  return { buffer, persistedBookId };
}

export async function saveBookToUint8Array(
  book: Book,
  compatContext: CompatContext | null = { passthroughFiles: {} }
): Promise<{ bytes: Uint8Array; persistedBookId: string }> {
  const { buffer, persistedBookId } = await saveBookToBuffer(book, compatContext);
  if (buffer instanceof Uint8Array) {
    return { bytes: buffer, persistedBookId };
  }
  return { bytes: new Uint8Array(buffer), persistedBookId };
}
